import React, { useState } from 'react'

function BudgetBreakdown({ clients }) {
  const [viewMode, setViewMode] = useState('ae') // 'ae' or 'company'

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`
    }
    return `$${amount}`
  }

  const formatFullCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0)
  }

  const getDataByAE = () => {
    const grouped = {}
    clients.forEach(client => {
      const ae = client.account_executive || 'Unassigned'
      if (!grouped[ae]) {
        grouped[ae] = { name: ae, clients: 0, activeClients: 0, linearBudget: 0, streamingBudget: 0 }
      }
      grouped[ae].clients++
      if (client.status === 'Active') grouped[ae].activeClients++
      grouped[ae].linearBudget += client.linear_budget || 0
      grouped[ae].streamingBudget += client.streaming_budget || 0
    })
    return Object.values(grouped)
      .map(g => ({ ...g, totalBudget: g.linearBudget + g.streamingBudget }))
      .sort((a, b) => b.totalBudget - a.totalBudget)
  }

  const getDataByCompany = () => {
    const grouped = {}
    clients.forEach(client => {
      const company = client.company || 'Unknown'
      if (!grouped[company]) {
        grouped[company] = { name: company, clients: 0, activeClients: 0, linearBudget: 0, streamingBudget: 0 }
      }
      grouped[company].clients++
      if (client.status === 'Active') grouped[company].activeClients++
      grouped[company].linearBudget += client.linear_budget || 0
      grouped[company].streamingBudget += client.streaming_budget || 0
    })
    return Object.values(grouped)
      .map(g => ({ ...g, totalBudget: g.linearBudget + g.streamingBudget }))
      .sort((a, b) => b.totalBudget - a.totalBudget)
  }

  const data = viewMode === 'ae' ? getDataByAE() : getDataByCompany()
  const maxBudget = Math.max(...data.map(d => d.totalBudget))
  const totalBudget = data.reduce((sum, d) => sum + d.totalBudget, 0)
  const totalLinear = data.reduce((sum, d) => sum + d.linearBudget, 0)
  const totalStreaming = data.reduce((sum, d) => sum + d.streamingBudget, 0)

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      padding: '24px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#111827',
          margin: 0
        }}>
          Budget Breakdown
        </h3>

        <div style={{ display: 'flex', gap: '4px' }}>
          <button
            onClick={() => setViewMode('ae')}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '500',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: viewMode === 'ae' ? '#111827' : '#f3f4f6',
              color: viewMode === 'ae' ? 'white' : '#6b7280'
            }}
          >
            By Account Executive
          </button>
          <button
            onClick={() => setViewMode('company')}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '500',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: viewMode === 'company' ? '#111827' : '#f3f4f6',
              color: viewMode === 'company' ? 'white' : '#6b7280'
            }}
          >
            By Company
          </button>
        </div>
      </div>

      {/* Summary row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px'
      }}>
        <div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Budget</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>{formatCurrency(totalBudget)}</div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Linear</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>{formatCurrency(totalLinear)}</div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Streaming</div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>{formatCurrency(totalStreaming)}</div>
        </div>
      </div>

      {/* Bar chart */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {data.map((item, index) => {
          const barWidth = maxBudget > 0 ? (item.totalBudget / maxBudget) * 100 : 0
          const linearWidth = item.totalBudget > 0 ? (item.linearBudget / item.totalBudget) * barWidth : 0
          const streamingWidth = item.totalBudget > 0 ? (item.streamingBudget / item.totalBudget) * barWidth : 0

          return (
            <div key={item.name}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '6px'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {item.name}
                  <span style={{
                    fontSize: '11px',
                    color: '#9ca3af',
                    fontWeight: '400'
                  }}>
                    ({item.clients} clients, {item.activeClients} active)
                  </span>
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#111827',
                  fontFamily: 'monospace'
                }}>
                  {formatFullCurrency(item.totalBudget)}
                </div>
              </div>

              {/* Stacked bar */}
              <div style={{
                height: '28px',
                backgroundColor: '#f3f4f6',
                borderRadius: '4px',
                overflow: 'hidden',
                display: 'flex'
              }}>
                <div
                  style={{
                    height: '100%',
                    width: `${linearWidth}%`,
                    backgroundColor: '#111827',
                    transition: 'width 0.3s ease'
                  }}
                  title={`Linear: ${formatFullCurrency(item.linearBudget)}`}
                />
                <div
                  style={{
                    height: '100%',
                    width: `${streamingWidth}%`,
                    backgroundColor: '#eab308',
                    transition: 'width 0.3s ease'
                  }}
                  title={`Streaming: ${formatFullCurrency(item.streamingBudget)}`}
                />
              </div>

              {/* Budget breakdown */}
              <div style={{
                display: 'flex',
                gap: '16px',
                marginTop: '4px',
                fontSize: '11px',
                color: '#6b7280'
              }}>
                <span>
                  <span style={{
                    display: 'inline-block',
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#111827',
                    borderRadius: '2px',
                    marginRight: '4px'
                  }} />
                  Linear: {formatCurrency(item.linearBudget)}
                </span>
                <span>
                  <span style={{
                    display: 'inline-block',
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#eab308',
                    borderRadius: '2px',
                    marginRight: '4px'
                  }} />
                  Streaming: {formatCurrency(item.streamingBudget)}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Legend */}
      <div style={{
        marginTop: '24px',
        paddingTop: '16px',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        gap: '24px',
        fontSize: '12px',
        color: '#6b7280'
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{
            width: '12px',
            height: '12px',
            backgroundColor: '#111827',
            borderRadius: '2px'
          }} />
          Linear Budget
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{
            width: '12px',
            height: '12px',
            backgroundColor: '#eab308',
            borderRadius: '2px'
          }} />
          Streaming Budget
        </span>
      </div>
    </div>
  )
}

export default BudgetBreakdown